import { useState, useCallback, useMemo } from 'react'
import { ClipboardList, PencilIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { type InformationForUseClause } from '@/shared/hooks/api/standards'
import { useUpdateUserStandardInformationForUse } from '@/shared/hooks/api/userStandards'
import {
  resolveInformationForUseAvailableLocales,
  resolveLocalizedInformationForUseField,
} from '@/shared/lib/standardEntityLocalizations'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useEntityDisplayLocales } from '../hooks/useEntityDisplayLocales'
import { useStandardSectionExtraction } from '../hooks/useStandardSectionExtraction'
import type { StandardDetailSectionProps } from '../standardDetailSectionProps'
import {
  informationForUseClauseCount,
  isInformationForUseConfirmedEmpty,
} from '../utils'
import { InformationForUseClauseForm } from './InformationForUseClauseForm'
import {
  type InformationForUseClauseFormData,
  validateInformationForUseClauseForm,
} from './InformationForUseClauseForm.schema'
import { SaveAsExtractionGoldButton } from './SaveAsExtractionGoldButton'
import { StandardEntityLocaleToggle } from './StandardEntityLocaleToggle'
import { StandardSectionEmptyState } from './StandardSectionEmptyState'
import { StandardSectionExtractControls } from './StandardSectionExtractControls'

export type StandardInformationForUseSectionProps = StandardDetailSectionProps

type DialogState = { type: 'edit'; clauseId: string } | { type: 'add' } | null

function clauseToFormData(
  clause: InformationForUseClause
): InformationForUseClauseFormData {
  return {
    number: clause.number?.trim() ?? '',
    title: clause.title?.trim() ?? '',
    content: clause.content?.trim() ?? '',
    fullStatement: clause.fullStatement?.trim() ?? '',
  }
}

function formDataToClause(formData: InformationForUseClauseFormData) {
  const number = formData.number.trim()
  const title = formData.title.trim() || undefined
  const content = formData.content.trim() || undefined
  const fullStatement = formData.fullStatement.trim() || undefined
  return {
    number: number || '',
    title,
    content,
    fullStatement,
  }
}

const emptyFormData: InformationForUseClauseFormData = {
  number: '',
  title: '',
  content: '',
  fullStatement: '',
}

export function StandardInformationForUseSection({
  standard,
  hasPdf,
  isPdfUploading,
  onUploadPdf,
}: StandardInformationForUseSectionProps) {
  const extraction = useStandardSectionExtraction(standard, 'informationForUse')
  const { t } = useTranslation('standardsLibrary')
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const updateInformationForUse = useUpdateUserStandardInformationForUse(
    standard.id
  )
  const [dialogState, setDialogState] = useState<DialogState>(null)
  const [formData, setFormData] =
    useState<InformationForUseClauseFormData>(emptyFormData)

  const currentClauses = (standard.informationForUseRequirements?.clauses ??
    []) as InformationForUseClause[]

  const openEditDialog = useCallback((clause: InformationForUseClause) => {
    setFormData(clauseToFormData(clause))
    setDialogState({ type: 'edit', clauseId: clause.id })
  }, [])

  const openAddDialog = useCallback(() => {
    setFormData(emptyFormData)
    setDialogState({ type: 'add' })
  }, [])

  const closeDialog = useCallback(() => {
    setDialogState(null)
  }, [])

  const updateClause = useCallback(() => {
    const validation = validateInformationForUseClauseForm(formData)
    if (!validation.success) {
      return
    }
    if (dialogState?.type !== 'edit') {
      return
    }
    const baseClauses = (standard.informationForUseRequirements?.clauses ??
      []) as InformationForUseClause[]
    const editIndex = baseClauses.findIndex(
      (clause) => clause.id === dialogState.clauseId
    )
    if (editIndex === -1) {
      return
    }
    const normalized = formDataToClause(formData)
    const newClauses = baseClauses.slice()
    newClauses[editIndex] = {
      ...normalized,
      id: baseClauses[editIndex].id,
    }
    updateInformationForUse.mutate(
      { clauses: newClauses },
      { onSuccess: closeDialog }
    )
  }, [
    formData,
    dialogState,
    standard.informationForUseRequirements?.clauses,
    updateInformationForUse,
    closeDialog,
  ])

  const createClause = useCallback(() => {
    const validation = validateInformationForUseClauseForm(formData)
    if (!validation.success) {
      return
    }
    const baseClauses = (standard.informationForUseRequirements?.clauses ??
      []) as InformationForUseClause[]
    const normalized = formDataToClause(formData)
    const newClause: InformationForUseClause = {
      ...normalized,
      id: crypto.randomUUID(),
    }
    const newClauses: InformationForUseClause[] = [...baseClauses, newClause]
    updateInformationForUse.mutate(
      { clauses: newClauses },
      { onSuccess: closeDialog }
    )
  }, [
    formData,
    standard.informationForUseRequirements?.clauses,
    updateInformationForUse,
    closeDialog,
  ])

  const deleteCurrentClause = useCallback(() => {
    if (dialogState?.type !== 'edit') {
      return
    }
    const baseClauses = (standard.informationForUseRequirements?.clauses ??
      []) as InformationForUseClause[]
    const newClauses = baseClauses.filter(
      (clause) => clause.id !== dialogState.clauseId
    )
    updateInformationForUse.mutate(
      { clauses: newClauses },
      { onSuccess: closeDialog }
    )
  }, [
    dialogState,
    standard.informationForUseRequirements?.clauses,
    updateInformationForUse,
    closeDialog,
  ])

  const isDialogOpen = dialogState !== null
  const isEdit = dialogState?.type === 'edit'
  const dialogTitle = isEdit ? t('detail.editClause') : t('detail.addClause')

  const isSaveDisabled = useMemo(() => {
    const validation = validateInformationForUseClauseForm(formData)
    return updateInformationForUse.isPending || !validation.success
  }, [formData, updateInformationForUse.isPending])

  const infoForUse = informationForUseClauseCount(standard)
  const confirmedEmpty = isInformationForUseConfirmedEmpty(standard)
  const hasCurrentClauses = currentClauses.length > 0
  return (
    <>
      <Card inert={extraction.extractDialogOpen || undefined}>
        <CardHeader className='pb-2'>
          <div className='flex flex-row items-center justify-between gap-4'>
            <CardTitle>{t('detail.informationForUseRequirements')}</CardTitle>
            <div className='flex flex-wrap items-center gap-2'>
              <StandardSectionExtractControls
                hasPdf={hasPdf}
                extraction={extraction}
              />
              <SaveAsExtractionGoldButton
                userStandardId={standard.id}
                organizationId={standard.organizationId}
                extractionType='INFORMATION_FOR_USE'
              />
              {hasCurrentClauses && (
                <Button variant='outline' size='sm' onClick={openAddDialog}>
                  {infoForUse === null || infoForUse === 0
                    ? t('detail.addInformationForUse')
                    : t('detail.addClause')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {!hasCurrentClauses ? (
          <StandardSectionEmptyState
            icon={<ClipboardList className='size-10' aria-hidden />}
            title={
              confirmedEmpty
                ? t('detail.empty.informationForUse.confirmedEmptyTitle')
                : t('detail.empty.informationForUse.title')
            }
            description={
              confirmedEmpty
                ? t('detail.empty.informationForUse.confirmedEmptyDescription')
                : hasPdf
                  ? t('detail.empty.informationForUse.descriptionWithPdf')
                  : t('detail.empty.informationForUse.descriptionNoPdf')
            }
            hasPdf={hasPdf}
            isPdfUploading={isPdfUploading}
            extraction={extraction}
            onUploadPdf={onUploadPdf}
            onManualAction={openAddDialog}
            manualActionLabel={
              infoForUse === null || infoForUse === 0
                ? t('detail.addInformationForUse')
                : t('detail.addClause')
            }
          />
        ) : (
          <ul className='divide-y border-t pt-2'>
            {currentClauses.map((clause) => {
              const availableLocales =
                resolveInformationForUseAvailableLocales(clause)
              const itemLocale = getDisplayLocale(clause.id)
              return (
                <li
                  key={clause.id}
                  className='flex flex-wrap items-start justify-between gap-2 px-4 py-2 first:pt-0'
                >
                  <div className='min-w-0 flex-1'>
                    <span className='font-medium'>{clause.number}. </span>
                    <p className='text-sm text-muted-foreground'>
                      {resolveLocalizedInformationForUseField(
                        clause,
                        itemLocale,
                        'fullStatement'
                      ) ||
                        clause.content ||
                        '—'}
                    </p>
                  </div>
                  <div className='flex shrink-0 items-center gap-2'>
                    <StandardEntityLocaleToggle
                      availableLocales={availableLocales}
                      selectedLocale={itemLocale}
                      onSelectLocale={(locale) =>
                        selectItemLocale(clause.id, locale)
                      }
                    />
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => openEditDialog(clause)}
                      aria-label={t('detail.edit')}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            {updateInformationForUse.isError && (
              <Alert variant='destructive'>
                <AlertDescription>
                  {updateInformationForUse.error?.message ??
                    t('detail.saveError')}
                </AlertDescription>
              </Alert>
            )}
            <InformationForUseClauseForm
              data={formData}
              onChange={setFormData}
            />
          </div>
          <DialogFooter className='flex-row justify-between sm:justify-between'>
            <div>
              {isEdit && (
                <Button
                  type='button'
                  variant='destructive'
                  onClick={deleteCurrentClause}
                  disabled={updateInformationForUse.isPending}
                >
                  {t('detail.deleteClause')}
                </Button>
              )}
            </div>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={closeDialog}
                disabled={updateInformationForUse.isPending}
              >
                {t('detail.cancel')}
              </Button>
              <Button
                onClick={isEdit ? updateClause : createClause}
                disabled={isSaveDisabled}
              >
                {updateInformationForUse.isPending
                  ? 'Saving…'
                  : t('detail.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
